import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Gift, DollarSign, Clock, Phone, Mail, AlertTriangle, UserCheck, XCircle } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const MarketingOffersEndgameSection = ({ onBack, onNext, nextSectionTitle, hasNext, onComplete, isCompleted }) => {

  const offerResponseSteps = [
    "Respond promptly to acknowledge the offer",
    "Thank the startup and express appreciation",
    "Politely request a few days to decide (if needed)"
  ];

  const negotiationReasons = [
    "The offer is lower than expected or advertised",
    "You've received higher offers elsewhere",
    "You have relevant experience at a higher salary"
  ];

  const negotiationApproaches = [
    {
      title: "Ask for a review clause",
      description: "e.g. salary revisit after probation"
    },
    {
      title: "Ask for a higher starting salary",
      description: "Direct approach if there's a significant gap"
    }
  ];

  const equityBasics = [
    "Most startups offer options with a 4-year vesting schedule and 1-year cliff",
    "You usually don't \"own\" any shares until after year 1",
    "You typically \"exercise\" your options when the company is acquired or raises a round",
    "Expect dilution – but this is normal and often outweighed by company growth"
  ];

  const contractChecklist = [
    {
      item: "Line manager",
      detail: "Is it who you expected?"
    },
    {
      item: "Salary",
      detail: "Does it match expectations and market?"
    },
    {
      item: "Holiday",
      detail: "Would usually expect at least 20 days + bank holidays"
    },
    {
      item: "Pension",
      detail: "Would usually expect a minimum 3% employer contribution"
    },
    {
      item: "Equity / options",
      detail: "Vesting schedule and % or value clearly stated"
    },
    {
      item: "Probation",
      detail: "Usually 3-6 months"
    }
  ];

  const followUpTimeline = [
    {
      time: "48 hours",
      action: "send a polite follow-up"
    },
    {
      time: "3+ days",
      action: "let the Jumpstart team know by emailing info@jumpstart-uk.com"
    }
  ];

  const withdrawalSteps = [
    "Let Jumpstart know first at info@jumpstart-uk.com",
    "Try to give at least 24 hours' notice if an interview is already scheduled",
    "Always be polite and professional – how you exit matters"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Offers & what comes next
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Navigate offers, negotiate terms, and make the right career decision.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="🎉 If you receive an offer: what to do next">
            <div className="space-y-8">
              <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-200">
                <h4 className="font-bold text-emerald-900 mb-4 text-lg flex items-center">
                  <Gift className="w-6 h-6 mr-3" />
                  First step:
                </h4>
                <p className="text-emerald-800 leading-relaxed">
                  Let Bethan (<a href="mailto:bethan@jumpstart-uk.com" className="text-[#02B491] hover:text-emerald-600 underline font-medium">bethan@jumpstart-uk.com</a>) know so the Jumpstart team can support you.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">Even if you're still unsure, always:</h4>
                <div className="grid gap-4">
                  {offerResponseSteps.map((step, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#02B491] rounded-xl flex items-center justify-center mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-slate-700 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-blue-800 text-sm leading-relaxed">
                    💡 If you're not sure about anything, ask to speak to your future manager again or hop on a clarifying call with the team. Jumpstart can help review your contract or offer before you sign.
                  </p>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="💰 Negotiating salary or options">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                It's completely okay to negotiate – just be respectful, clear and constructive.
              </p>

              <div className="bg-amber-50 rounded-2xl p-8 border border-amber-200">
                <h4 className="font-bold text-amber-900 mb-6 text-lg">Reasons you might want to negotiate:</h4>
                <div className="grid gap-4">
                  {negotiationReasons.map((reason, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                      <p className="text-amber-800 leading-relaxed">{reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              <InfoCard
                icon={Phone}
                title="Important tip"
                description="If you're negotiating, always try to do it over a call – tone and intent come across much better than in writing."
                type="warning"
              />

              <div className="grid gap-8 md:grid-cols-2">
                {negotiationApproaches.map((approach, index) => (
                  <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200">
                    <h4 className="text-lg font-bold text-gray-900 mb-3">{index + 1}. {approach.title}</h4>
                    <p className="text-slate-700 leading-relaxed">{approach.description}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">💬 Suggested phrasing:</h4>
                <div className="space-y-6">
                  <InfoCard
                    title="Option 1"
                    description="I'm really excited about the role and would love to accept, but I was hoping to discuss whether there's room to increase the starting salary or include a review after 6 months."
                    type="tip"
                  />
                  <InfoCard
                    title="Option 2"
                    description="I've previously earned £X and while I'm excited about this opportunity, I was hoping for something in the £X-Y range – is there flexibility here?"
                    type="tip"
                  />
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="📈 Understanding startup equity and options">
            <div className="space-y-8">
              <div className="bg-purple-50 rounded-2xl p-8 border border-purple-200">
                <h4 className="font-bold text-purple-900 mb-6 text-lg">If your offer includes equity or options, key things to know:</h4>
                <div className="grid gap-4">
                  {equityBasics.map((basic, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-xl flex items-center justify-center mt-1">
                        <DollarSign className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-purple-800 leading-relaxed">{basic}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 rounded-2xl p-8 border border-green-200">
                <h4 className="font-bold text-green-900 mb-4 text-lg">💰 Benchmarks:</h4>
                <p className="text-green-800 leading-relaxed mb-4">
                  Options for early-stage roles tend to range from <span className="font-semibold">£2,000 - £30,000 value</span> OR <span className="font-semibold">0.075% - 0.5% equity</span>
                </p>
                <p className="text-green-700 text-sm">
                  Want help decoding your options offer? Just ask the Jumpstart team.
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="⏰ Sequencing offers or buying yourself time">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                If you've received an offer but are still waiting on others, here are two sample emails:
              </p>

              <div className="grid gap-8 md:grid-cols-2">
                <div className="bg-blue-50 rounded-2xl p-8 border border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-4 text-lg flex items-center">
                    <Clock className="w-6 h-6 mr-3" />
                    To stall the startup who offered:
                  </h4>
                  <InfoCard
                    title="Email template"
                    description="Thanks so much for the offer – I'm really excited. I'm currently wrapping up interviews with a couple of other teams and wanted to see if I could have until [date] to make a decision. I really appreciate the flexibility!"
                    type="tip"
                  />
                </div>

                <div className="bg-green-50 rounded-2xl p-8 border border-green-200">
                  <h4 className="font-bold text-green-900 mb-4 text-lg flex items-center">
                    <UserCheck className="w-6 h-6 mr-3" />
                    To accelerate another startup:
                  </h4>
                  <InfoCard
                    title="Email template"
                    description="Just wanted to flag that I've received an offer elsewhere and they've asked for a decision by [date]. If I were offered a role here, I'd be very keen to accept – is there any chance we could move things forward?"
                    type="success"
                  />
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="📋 Understanding your contract before signing">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Double check the offer or contract for:
              </p>
              
              <div className="grid gap-6 md:grid-cols-2">
                {contractChecklist.map((item, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 border border-gray-200">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#02B491] rounded-xl flex items-center justify-center mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{item.item}</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                <p className="text-blue-800 text-sm">
                  💡 Need help reviewing? Reach out to Bethan at <a href="mailto:bethan@jumpstart-uk.com" className="text-[#02B491] hover:text-blue-600 underline font-medium">bethan@jumpstart-uk.com</a>
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="📞 Following up if you haven't heard back">
            <div className="space-y-8">
              <div className="bg-orange-50 rounded-2xl p-8 border border-orange-200">
                <h4 className="font-bold text-orange-900 mb-6 text-lg">If it's been:</h4>
                <div className="grid gap-4">
                  {followUpTimeline.map((item, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-xl flex items-center justify-center mt-1">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold text-orange-900">{item.time}</span>
                        <span className="text-orange-800"> → {item.action}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <InfoCard
                icon={Mail}
                title="Example follow-up email"
                description="Hi [name], just checking in after our interview last [day] – I really enjoyed our conversation and wanted to see if there are any updates on next steps. Let me know if you need anything else from me!"
                type="tip"
              />
            </div>
          </StepCard>

          <StepCard title="🚪 How to withdraw from a process (at any stage)">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                If the role no longer feels like the right fit:
              </p>

              <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
                <h4 className="font-bold text-red-900 mb-6 text-lg">Key principles:</h4>
                <div className="grid gap-4">
                  {withdrawalSteps.map((step, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-xl flex items-center justify-center mt-1">
                        <AlertTriangle className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-red-800 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-8 md:grid-cols-1">
                <InfoCard
                  title="Example email (during interview process)"
                  description="Hi [name], thank you again for the time and conversations so far. After reflecting on the opportunity, I've decided to focus my search elsewhere and would like to withdraw from the process. I really appreciate your time and hope to stay in touch."
                  type="warning"
                />
                
                <InfoCard
                  title="Example email (post-offer - use to book a call)"
                  description="Hi [name], I really appreciate the offer and all the time the team has invested in the process. I'd love to find a time to jump on a quick call to chat through where my head is at before making a final decision. Would [times] work?"
                  type="warning"
                />
              </div>

              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200 text-center">
                <p className="text-emerald-800 font-medium">
                  If in doubt we can help – just reach out to <span className="font-bold">info@jumpstart-uk.com</span>
                </p>
              </div>
            </div>
          </StepCard>

        </div>
      </div>

        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default MarketingOffersEndgameSection; 